import { AdBiddingProvider, IAdBiddingProviderConfig } from '../../../'
import { IConfiguration } from '../../../../../../domain/conf'
import { IdentityEvent } from '../../../../../../services/common/events/identity'
import { Errors } from '../../../../../../utils/common'
import { logger } from '../../../../../../utils/common/logger'
import { DOMUtils } from '../../../../../../utils/dom'
import { IAd } from '../../../../ad'
import { AdBiddingBase } from '../base'

interface IWindow {
    googletag: any
    setTimeout: any
    pbjs: any
    IntentIqObject: any
}

declare let window: IWindow

class Magnite extends AdBiddingBase {
    public requestAds: undefined
    public intentIqReady = false
    public IIQURLPath: string
    public IIQPartnerId: number | undefined
    public IIQAbPercentage: number
    public IIQBrowserBlackList: string[]
    public IIQTimeoutInMillis: number
    public IIQDomain: string

    protected URLPath: string
    protected waitForBidder = true
    protected failSafeTimeout = 3500
    protected pbjs: any
    protected IntentIqObject: any

    constructor() {
        super()
        this.URLPath = '//micro.rubiconproject.com/prebid/dynamic/14648.js' // TODO '14648' should come from page-data
    }

    public static OnMagniteError() {
        logger('error', 'Magnite: failed to load script')
        window.pbjs.error = true
        document.dispatchEvent(new Event(Errors.MAGNITE_ERROR))
    }

    public initializeIntentIQ(conf: IConfiguration): void {
        const DEFAULT_AB_PERCENTAGE = 95
        const DEFAULT_BROWSER_BLACKLIST: string[] = []
        const DEFAULT_TIMEOUT_IN_MILLIS = 1000
        const DEFAULT_DOMAIN = ''

        const femUrl = conf.analytics?.tags?.femUrl || ''
        this.IIQURLPath = femUrl ? `${femUrl}IIQUniversalID.js` : ''
        this.IIQPartnerId = conf.analytics?.tags?.intentiq?.partner_id
        this.IIQAbPercentage = conf.analytics?.tags?.intentiq?.ab_percentage || DEFAULT_AB_PERCENTAGE
        const browserBlackListString = (conf.analytics?.tags?.intentiq?.browser_blackList || '').trim()
        this.IIQBrowserBlackList = browserBlackListString
            ? browserBlackListString.split(',')
            : DEFAULT_BROWSER_BLACKLIST
        this.IIQTimeoutInMillis = conf.analytics?.tags?.intentiq?.timeout_in_millis || DEFAULT_TIMEOUT_IN_MILLIS
        this.IIQDomain = conf.analytics?.domain || DEFAULT_DOMAIN
    }

    public reportExternalWin(): void {
        // ref: https://js.documents.intentiq.com/integration-workflow/winning-impression-reporting
        window.pbjs.que.push(() => {
            window.pbjs.onEvent('bidWon', (bid: any) => {
                if (bid.status === 'rendered') {
                    const reportData = {
                        bidderCode: bid.bidderCode,
                        biddingPlatformId: 1,
                        cpm: bid.cpm,
                        currency: bid.currency,
                        originalCpm: bid.originalCpm,
                        originalCurrency: bid.originalCurrency,
                        placementId: bid.adUnitCode,
                        prebidAuctionId: bid.auctionId,
                        status: 'rendered',
                    }
                    this.IntentIqObject.reportExternalWin(reportData)
                    logger('debug', 'IntentIq external win reported', reportData)
                    // enabling logger breaks code on safari with err: RangeError: Maximum call stack size exceeded.
                    console.debug('IntentIq external win reported')
                } else {
                    logger('debug', `IntentIq external win not reported, bid status: ${bid.status}`)
                }
            })
        })
    }

    public loadIntentIq(): void {
        // ref: https://js.documents.intentiq.com/integration-workflow/add-an-intentiq-tag-to-your-webpage
        DOMUtils.loadScript(this.IIQURLPath, undefined, undefined, () => {
            logger('debug', 'IntentIq script loaded')
            const iiqCallbackMethod = (eids: any) => {
                logger('debug', 'IntentIq callback, eids:', eids)
                this.intentIqReady = true
                document.dispatchEvent(new Event(IdentityEvent.INTENTIQ_READY))
            }
            this.IntentIqObject = new window.IntentIqObject({
                ABTestingConfigurationSource: 'IIQServer',
                callback: iiqCallbackMethod,
                domainName: this.IIQDomain,
                gamObjectReference: window.googletag,
                gamParameterName: 'intent_iq_group',
                manualWinReportEnabled: true,
                partner: this.IIQPartnerId,
                pbjs: window.pbjs,
                shouldSetPPID: true,
                timeoutInMillis: this.IIQTimeoutInMillis,
                vrBrowserBlackList: this.IIQBrowserBlackList
            })
        })
        window.setTimeout(() => {
            if (!this.intentIqReady) {
                this.intentIqReady = true
                document.dispatchEvent(new Event(IdentityEvent.INTENTIQ_READY))
            }
        }, this.failSafeTimeout)
    }

    public initializeBidding(confs: IAdBiddingProviderConfig[]): void {

        const conf = confs.find((_) => _.client === AdBiddingProvider.MAGNITE)
        if (conf) {
            window.pbjs = window.pbjs || {}
            window.pbjs.que = window.pbjs.que || []
            if (this.IIQPartnerId && this.IIQURLPath && !this.isBotCrawler()) {
                this.reportExternalWin()
                this.loadIntentIq()
            } else {
                this.intentIqReady = true
                document.dispatchEvent(new Event(IdentityEvent.INTENTIQ_READY))
            }
            DOMUtils.loadScript(this.URLPath, undefined, Magnite.OnMagniteError)
            logger(`debug`, `Initialize Magnite bidder: ${this.URLPath}`)
        }
    }

    public async requestAdsPromise(ads?: IAd[], slot?: any): Promise<any> {
        logger('info', 'optional ads passed to requestAdsPromise', ads)
        return new Promise((resolve: any, _): void => {
            // error loading Magnite/Prebid.js
            if (window.pbjs.error) {
                resolve()
            }
            document.addEventListener(Errors.MAGNITE_ERROR, () => {
                resolve()
            })
            const requestBids = () => {
                window.pbjs.que.push(() => {
                    const adUnitCodes = ads.map((ad) => ad.id)
                    const gptSlotObjects = slot || ads.map((ad) => ad.slot)

                    window.pbjs.rp.requestBids({
                        // specify ad IDs to request
                        adUnitCodes,
                        callback: () => {
                            /* istanbul ignore next */
                            logger('info', 'Magnite bidder has resolved')
                            resolve()
                        },
                        gptSlotObjects
                    })
                    window.setTimeout(resolve, this.failSafeTimeout)
                    // can remove event listener safely as we are already in the pbjs.que
                    /* istanbul ignore next */
                    document.removeEventListener(Errors.MAGNITE_ERROR, () => {
                        resolve()
                    })
                })
            }
            if (this.intentIqReady) {
                requestBids()
            } else {
                let bidsRequested = false
                document.addEventListener(IdentityEvent.INTENTIQ_READY, () => {
                    if (!bidsRequested) {
                        bidsRequested = true
                        requestBids()
                    }
                })
            }
        })
    }

    private isBotCrawler(): boolean {
        try {
            if (typeof navigator === 'undefined') {
                return false
            }

            const userAgent = (navigator.userAgent || '').toLowerCase()
            const knownCrawlerPattern = /(bot|crawler|spider|slurp|bingpreview|mediapartners-google|adsbot|google-structured-data-testing-tool|headlesschrome|prerender)/

            return knownCrawlerPattern.test(userAgent) || !!navigator.webdriver
        } catch (error) {
            logger('warn', 'Magnite: failed to detect bot crawler', error)
            return false
        }
    }
}

export default Magnite
